import { getTenantFromHostname } from "@/utils/get-tenant-from-hostname";
import { ArrowBack } from "@mui/icons-material";
import { Box, Button, InputAdornment, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Input from "../../components/Input";
import { MailOutlineIcon } from "../../components/svg-icons/material-icons";
import { appPaths } from "../../constants/paths";
import { FORGOT_PASSWORD_FORM_INITIAL_VALUES } from "../../constants/setting-initial-values";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useResendLink } from "../../queries/use-resend-link";
import { getErrorSnackbar, getSuccessSnackbar } from "../../utils/utils";

const ForgotPasswordV2 = () => {
  const navigate = useNavigate();
  const urlTenant = location.hostname?.split(".")?.[0];
  const tenantName = urlTenant?.includes("_")
    ? urlTenant.split("_").slice(1).join("_")
    : urlTenant || "";

  const changePasswordValidationSchema = Yup.object({
    email: Yup.string()
      .email()
      .matches(/.*@.+\..+/, "Email address must be a valid Email")
      .label("Email")
      .required(),
    // tenant: Yup.string()
    //   .when([], {
    //     is: () => urlTenant === "app",
    //     then: (schema) => schema.notRequired(),
    //     otherwise: (schema) => schema.required(),
    //   })
    // .label("Tenant"),
  });

  const params = new URLSearchParams(window.location.search);
  const { mutateAsync: forgetPassword } = useResendLink();
  const showSnackbar = useSnackbar();
  const {
    values,
    touched,
    errors,
    submitForm,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    initialValues: FORGOT_PASSWORD_FORM_INITIAL_VALUES,
    validationSchema: changePasswordValidationSchema,
    onSubmit: handleForgetPass,
  });

  const hostTenant = getTenantFromHostname();
  const tenantId = hostTenant ?? params.get("realm") ?? undefined;

  useEffect(() => {
    tenantId && setFieldValue("tenant", tenantId);
  }, [tenantId]);

  function handleForgetPass() {
    forgetPassword({
      linkType: "reset",
      setPasswordRequest: {
        email: values.email,
      },
      xTENANTID: tenantId,
    })
      .then((res) => {
        // resetForm();
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Title & Subtitle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          mb: "32px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "#1D1D1D",
            fontSize: "20px",
            fontFamily: "Inter",
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          Forgot Password
        </Typography>
        <Typography
          sx={{
            color: "#727272",
            fontSize: "14px",
            fontFamily: "Inter",
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          Enter your registered Email Address, to receive the reset password
          link
        </Typography>
      </Box>

      {/* Form */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          width: "100%",
        }}
      >
        <Input
          label="Email"
          textFieldProps={{
            placeholder: `yourname@${tenantName}.com`,
            onChange: handleChange,
            onBlur: handleBlur,
            name: "email",
            value: values.email,
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <MailOutlineIcon sx={{ color: "#B4B5B5", fontSize: 16 }} />
                </InputAdornment>
              ),
            },
          }}
          error={touched.email && errors.email}
        />

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          type="submit"
          onClick={submitForm}
          sx={{
            py: "16px",
            px: "18px",
            fontSize: "16px",
            fontFamily: "Inter",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "4px",
            backgroundColor: "#104E84",
            border: "1px solid #104E84",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            lineHeight: 1.25,
            "&:hover": { backgroundColor: "#0D3F6B" },
          }}
        >
          Submit
        </Button>

        {/* Back to Login Button */}
        <Button
          variant="outlined"
          fullWidth
          onClick={() => navigate(appPaths.login)}
          sx={{
            py: "16px",
            px: "18px",
            fontSize: "16px",
            fontFamily: "Inter",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "4px",
            backgroundColor: "#EEF7FF",
            border: "1px solid #104E84",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
            color: "#104E84",
            lineHeight: 1.25,
            gap: "8px",
            "&:hover": {
              backgroundColor: "#DFF0FF",
              border: "1px solid #104E84",
            },
          }}
        >
          <ArrowBack sx={{ fontSize: 20 }} />
          Back to sign in page
        </Button>
      </Box>
    </Box>
  );
};

export default ForgotPasswordV2;
